import { Injectable, inject } from '@angular/core';

import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { take, Observable, map, combineLatest } from 'rxjs';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';

import { CropCyclePhase, StepStatus, TgaState, TgaSteps } from '../shared/models/models';
import { StepperActions } from '../store/tga.actions';
import { selectLastGenericStepCompleted, selectLastSpecificStepCompleted, selectActiveStepName } from '../store/tga.selectors';

@Injectable({
  providedIn: 'root',
})
export class StepperService {
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<TgaState>>(Store);

  private readonly phaseOrder: TgaSteps[] = [
    TgaSteps.General,
    TgaSteps.Soil,
    TgaSteps.Planting,
    TgaSteps.Growing,
    TgaSteps.Evaluation,
  ];

  private readonly defaultPhases: CropCyclePhase[] = [
    {
      translationKey: 'Prepare',
      steps: [
        { translationKey: TgaSteps.General, status: StepStatus.Active },
        { translationKey: TgaSteps.Soil, status: StepStatus.Inactive },
      ],
    },
    {
      translationKey: 'Cultivation',
      steps: [
        { translationKey: TgaSteps.Planting, status: StepStatus.Inactive },
        { translationKey: TgaSteps.Growing, status: StepStatus.Inactive },
      ],
    },
    {
      translationKey: 'Evaluation',
      steps: [{ translationKey: TgaSteps.Evaluation, status: StepStatus.Inactive, special: true }],
    },
  ];

  getPhases(): CropCyclePhase[] {
    return JSON.parse(JSON.stringify(this.defaultPhases));
  }

  getStartingStepNumber(phases: CropCyclePhase[], phaseIndex: number): number {
    let stepCount = 0;
    for (let i = 0; i < phaseIndex; i++) {
      stepCount += phases[i].steps.length;
    }
    return stepCount + 1;
  }

  getAbsoluteStepNumber(phases: CropCyclePhase[], phaseIndex: number, stepIndex: number): number {
    return this.getStartingStepNumber(phases, phaseIndex) + stepIndex;
  }

  getTotalSteps(phases: CropCyclePhase[]): number {
    return phases.reduce((total, phase) => total + phase.steps.length, 0);
  }

  findActiveStep(
    phases: CropCyclePhase[],
  ): { phaseIndex: number; stepIndex: number; absoluteStepNumber: number } | null {
    for (let i = 0; i < phases.length; i++) {
      const phase = phases[i];
      for (let j = 0; j < phase.steps.length; j++) {
        if (phase.steps[j].status === StepStatus.Active) {
          return {
            phaseIndex: i,
            stepIndex: j,
            absoluteStepNumber: this.getAbsoluteStepNumber(phases, i, j),
          };
        }
      }
    }
    return null;
  }

  getNextStepTranslationKey(phases: CropCyclePhase[]): TgaSteps | null {
    const activeStep = this.findActiveStep(phases);
    if (!activeStep) {
      return null;
    }

    const { phaseIndex, stepIndex } = activeStep;

    if (stepIndex + 1 < phases[phaseIndex].steps.length) {
      return phases[phaseIndex].steps[stepIndex + 1].translationKey;
    } else if (phaseIndex + 1 < phases.length && phases[phaseIndex + 1].steps.length > 0) {
      return phases[phaseIndex + 1].steps[0].translationKey;
    }

    return null;
  }

  getUpdatedPhasesForStepClick(phases: CropCyclePhase[], clickedStepNumber: number): CropCyclePhase[] {
    return phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step, stepIndex) => {
        const phaseIndex = phases.indexOf(phase);
        const absoluteStepNumber = this.getAbsoluteStepNumber(phases, phaseIndex, stepIndex);

        if (absoluteStepNumber === clickedStepNumber) {
          return { ...step, status: StepStatus.Active };
        } else if (absoluteStepNumber < clickedStepNumber) {
          return { ...step, status: StepStatus.Completed };
        } else {
          return { ...step, status: StepStatus.Inactive };
        }
      }),
    }));
  }

  getStepNameByAbsoluteNumber(absoluteNumber: number): TgaSteps | null {
    let currentStep = 1;

    for (const phase of this.defaultPhases) {
      for (const step of phase.steps) {
        if (currentStep === absoluteNumber) {
          return step.translationKey;
        }
        currentStep++;
      }
    }

    return null;
  }

  getNextStepName(currentStepName: TgaSteps): TgaSteps | null {
    let foundCurrent = false;

    for (const phase of this.defaultPhases) {
      for (const step of phase.steps) {
        if (foundCurrent) {
          return step.translationKey;
        }

        if (step.translationKey === currentStepName) {
          foundCurrent = true;
        }
      }
    }

    return null;
  }

  moveToStep(stepName: TgaSteps): void {

    if (!this.isValidStep(stepName)) {
      stepName = TgaSteps.General;
    }

    const hasUnsavedChanges = this.unsavedChangesService.hasUnsavedChanges();

    if (hasUnsavedChanges) {
      this.dialogService
        .confirm({
          dialogCancelDefault: true,
          title: this.localeService.translate('TGA.UnsavedChanges.Title'),
          confirmButtonText: this.localeService.translate('TGA.UnsavedChanges.Leave'),
          confirmButtonIcon: false,
          cancelButtonText: this.localeService.translate('TGA.UnsavedChanges.Stay'),
          text: this.localeService.translate('TGA.UnsavedChanges.Message'),
          dismissAtClose: true,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.unsavedChangesService.markFormCompleted();
            this.store.dispatch(StepperActions.updateActiveStep({ stepName }));
          }
        });
    } else {
      this.store.dispatch(StepperActions.updateActiveStep({ stepName }));
    }
  }

  isValidStep(stepName: TgaSteps): boolean {
    for (const phase of this.defaultPhases) {
      for (const step of phase.steps) {
        if (step.translationKey === stepName) {
          return true;
        }
      }
    }
    return false;
  }

  moveToNextStep(): void {
    const activeStepName = this.store.select(selectActiveStepName);
    activeStepName.pipe(take(1)).subscribe((currentStep) => {
      const nextStep = this.getNextStepName(currentStep);
      if (nextStep) {
        this.moveToStep(nextStep);
      }
    });
  }


  // Get the absolute step number for a step name
  getAbsoluteStepNumberByName(stepName: TgaSteps): number {
    let stepNumber = 1;

    for (const phase of this.defaultPhases) {
      for (const step of phase.steps) {
        if (step.translationKey === stepName) {
          return stepNumber;
        }
        stepNumber++;
      }
    }

    return 1; // Default to first step if not found
  }

  getPhasesFromRoute(questionnaireType$: Observable<string | undefined>): Observable<CropCyclePhase[]> {
    const isSpecific$ = questionnaireType$.pipe(
      map((type) => {
        if (type !== 'specific' && type !== 'generic') {
          return null;
        }
        return type === 'specific';
      }),
    );

    return combineLatest([
      isSpecific$,
      this.store.select(selectLastSpecificStepCompleted),
      this.store.select(selectLastGenericStepCompleted),
    ]).pipe(
      map(([isSpecific, specificStep, genericStep]) => {
        if (isSpecific === null) {
          return this.getPhases();
        }
        const lastCompletedStep = isSpecific ? specificStep : genericStep;
        return this.computePhasesWithState(lastCompletedStep);
      }),
    );
  }

  getPhasesForQuestionnaire(isSpecific: boolean) {
    const selector = isSpecific
      ? selectLastSpecificStepCompleted
      : selectLastGenericStepCompleted;

    return this.store.select(selector).pipe(
      map((lastCompletedStep) => this.computePhasesWithState(lastCompletedStep)),
    );
  }

  isStepEnabled(stepName: TgaSteps, lastCompletedStep: TgaSteps | null): boolean {
    if (stepName === TgaSteps.General) {
      return true;
    }

    if (!lastCompletedStep) {
      return false;
    }
    const currentIndex = this.phaseOrder.indexOf(stepName);
    const lastCompletedIndex = this.phaseOrder.indexOf(lastCompletedStep);

    if (stepName === TgaSteps.Evaluation) {
      return lastCompletedIndex >= this.phaseOrder.indexOf(TgaSteps.Planting);
    }
    return currentIndex <= lastCompletedIndex + 1;
  }


  private computePhasesWithState(lastCompletedStep: TgaSteps | null): CropCyclePhase[] {
    const phases = this.getPhases();

    phases.forEach((phase) => {
      phase.steps.forEach((step) => {
        const stepName = step.translationKey;
        const isEnabled = this.isStepEnabled(stepName, lastCompletedStep);
        if (isEnabled) {
          step.enabled = true;
        } else {
          step.enabled = false;
        }
      });
    });
    return phases;
  }
}
